import * as React from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form, useFormikContext } from 'formik';
import {
    Checkbox,
    FormControlLabel,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { makeStyles } from '@mui/styles';

import { getSubmissionDetails } from 'application/redux/selectors';
import { Spacing, Book, Page } from 'components/Layout';
import { LabeledData, TextInput } from 'components/Input';

import LrButton from 'components/LrButton';
import { UwAdjustmentReviewPage } from 'components/Underwriting';
import { PRODUCT_LINE_TYPES, ROUTES, STATUS_TYPES } from 'constants/index';
import { currencyMask, currencyMaskWithNegatives } from 'helpers/masks';
import { currencyToNumber, formatDollar, formatPercent } from 'helpers/formatters';
import { sendAdjustmentReview } from 'services/endorsements';
import { getTopLevelUserGroup } from 'application/redux/selectors';
import SettingsIcon from '@mui/icons-material/Settings';
import EndorsmentNote from 'components/SubmissionDetails/EndorsementNote';
import DeclineReviewDialog from '../DeclineReviewDialog';
import { adjustmentReviewSchema } from '../ValidationSchema';
import { getIsUnderwriterRole } from 'helpers/roles';
import { AdjustmentReviewData, AdjustmentReviewLocationData, UwLocationReview } from 'types/underwriting.type';
import { fetchAdjustmentReviewData } from 'services/uwReview';
import { useEffect } from 'react';
import {
    calculateEndorsementPremium,
    calculateAdditionalBrokerPremium,
    calculateAnnualizedPremiumFromRateAndLimit,
    addTriaPremiumToRatedPremium,
    calculateTotalPremiumFromAnnualizedPremium,
} from 'helpers/calculations';
import { TaxAndFeesInfo } from 'components/TaxAndFees';
import { CircularLoader } from 'components/Loader';
import { TaxData } from 'components/TaxAndFees/components/TaxAndFeesInfoDialog';
import LrDialog from 'components/LrDialog';
import UWDetailsAdjustmentDialog from './UWDetailsAdjustmentDialog';
import { AdjustmentReviewRequest } from '../../../../../../types/endorsements.type';

interface Props {
    history: any;
}

interface FormData {
    narrative: string;
    premium: string | number;
    policyFee?: string;
    professionalFee?: string;
    slaFee?: string | number;
    slaTax?: string | number;
}

type UpdateTaxesFunc = (
    taxDataParam: TaxData,
    totalFees: number,
    totalPrem: number,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => void;

const AutoUpdateForm = (formProps: {
    totalPrem: number;
    taxData: TaxData;
    isAutoCalc: boolean;
    updateTaxes: UpdateTaxesFunc;
    isBrokerCommissionRated: boolean;
    brokerCommissionPercentage: number;
}) => {
    const { values, setFieldValue } = useFormikContext<FormData>();
    React.useEffect(() => {
        if (formProps.isAutoCalc) {
            const totalFees =
                (values.policyFee ? Number(currencyToNumber(values.policyFee)) : 0) +
                (values.professionalFee ? Number(currencyToNumber(values.professionalFee)) : 0);
            const totalPrem = calculateAdditionalBrokerPremium(
                formProps.isBrokerCommissionRated,
                formProps.totalPrem,
                formProps.brokerCommissionPercentage
            );
            formProps.updateTaxes(formProps.taxData, totalFees, totalPrem, setFieldValue);
        }
        setFieldValue('premium', formProps.totalPrem);
    }, [values.policyFee, values.professionalFee, formProps.totalPrem]);

    return null;
};

const AdjustmentReview: React.FunctionComponent<Props> = (props) => {
    const { history } = props;

    // Global State
    const submission = useSelector(getSubmissionDetails);
    const group = useSelector(getTopLevelUserGroup);

    // Local State
    const isUnderwriter = getIsUnderwriterRole(group);
    const [showDeclineDialog, setShowDeclineDialog] = React.useState(false);
    const [isAutoCalc, setIsAutoCalc] = React.useState(false);
    const [adjustmentReviewData, setAdjustmentReviewData] = React.useState<AdjustmentReviewData>(
        {} as AdjustmentReviewData
    );
    const [totalPremium, setTotalPremium] = React.useState<number>(0);
    const [action, setAction] = React.useState({ error: false, success: false, inProgress: false });
    const [taxData, setTaxData] = React.useState<TaxData>({});
    const isReadOnly = submission.statusType.code !== STATUS_TYPES.ENDORSEMENT_REQUEST_REVIEW || !isUnderwriter;
    const [taxesLoading, setTaxesLoading] = React.useState(!submission?.producer?.isFileTaxes);
    const [initialValues, setInitialValues] = React.useState<FormData>({
        narrative: '',
        premium: '',
        policyFee: undefined,
        professionalFee: undefined,
        slaFee: undefined,
        slaTax: undefined,
    });
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [detailsOpen, setDetailsOpen] = React.useState(false);
    const [submittedValues, setSubmittedValues] = React.useState<{ slaFee?: number; slaTax?: number } | undefined>();
    const [applyChangeRetroactive, setApplyChangeRetroactive] = React.useState<boolean>(false);

    const fetchData = async () => {
        if (submission?.id) {
            const data = await fetchAdjustmentReviewData(submission.id);
            if (data) {
                // initialize the "origRate"
                data.locationData.forEach((ld) => (ld.origRate = ld.finalRate));
                setApplyChangeRetroactive(data.isRateRetroactive);
                setAdjustmentReviewData(data);
                updateDataPremium(data);
                setInitialValues({ ...initialValues, premium: data.totalPrem });
            }
        }
    };

    const updateDataPremium = (data: AdjustmentReviewData) => {
        let totalPrem = 0;
        data.locationData.forEach((d: AdjustmentReviewLocationData) => {
            // additional premium is (delta TIV * new rate) + (old TIV * delta rate)
            const newTivPremium = addTriaPremiumToRatedPremium(
                calculateAnnualizedPremiumFromRateAndLimit(d.finalRate, d.effectiveTiv),
                d.triaPercentage
            );
            const newRateOldTivPremium = addTriaPremiumToRatedPremium(
                calculateAnnualizedPremiumFromRateAndLimit(d.finalRate - d.origRate, d.origTiv),
                d.triaPercentage
            );
            // This value is annualized
            d.grossPrem = newTivPremium + (applyChangeRetroactive ? newRateOldTivPremium : 0);
            // We need the total premium for calculating the Endorsement premium
            const totalGrossPrem = calculateTotalPremiumFromAnnualizedPremium(
                d.grossPrem,
                data.origEffectiveDate,
                data.origExpirationDate
            );
            d.proRataPrem = calculateEndorsementPremium(
                totalGrossPrem,
                data.origEffectiveDate,
                data.origExpirationDate,
                data.newEffectiveDate,
                data.newExpirationDate
            );
            if (d.propertyReview?.endorsementPremium) {
                d.propertyReview.endorsementPremium = d.proRataPrem;
                d.propertyReview.totalPremium = d.grossPrem;
            }
            totalPrem += d.proRataPrem;
        });
        data.totalPrem = totalPrem;
    };

    useEffect(() => {
        if (adjustmentReviewData.locationData) {
            updateDataPremium(adjustmentReviewData);
            setTotalPremium(adjustmentReviewData.totalPrem);
        }
    }, [adjustmentReviewData, applyChangeRetroactive]);

    useEffect(() => {
        fetchData();
    }, [submission]);

    const doSubmit = async (values: any) => {
        try {
            const premiums = {};
            const propertyReviews = {};
            adjustmentReviewData.locationData.forEach((ld) => {
                premiums[ld.locationId] = ld.proRataPrem;
                propertyReviews[ld.locationId] = {
                    ...ld.propertyReview,
                    productLineTypeCode: PRODUCT_LINE_TYPES.COMMERCIAL_PROPERTY,
                };
            });
            const payload: AdjustmentReviewRequest = {
                underwriterReview: {
                    premium: Number(currencyToNumber(values.premium)),
                    policyFee: Number(currencyToNumber(values.policyFee)),
                    professionalFee: Number(currencyToNumber(values.professionalFee)),
                    slaFee: Number(currencyToNumber(values.slaFee)),
                    slaTax: Number(currencyToNumber(values.slaTax)),
                },
                premiums: premiums,
                narrative: values.narrative,
                propertyReviews: propertyReviews,
                isRateRetroactive: applyChangeRetroactive,
            };
            await sendAdjustmentReview(submission.id, payload);
            history.push(ROUTES.submissions());
        } catch (e) {
            setAction({
                ...action,
                error: true,
                success: false,
                inProgress: false,
            });
        }
    };

    const submit = (values: any) => {
        setSubmittedValues(values);
        if (!values.slaTax && !values.slaFee) {
            setConfirmOpen(true);
        } else {
            doSubmit(values);
        }
    };

    const confirmSubmit = () => {
        setConfirmOpen(false);
        doSubmit(submittedValues);
    };

    const useStyles = makeStyles({
        table: {
            minWidth: 650,
        },
        retroLabel: {
            marginBottom: '5px',
            '& .MuiFormControlLabel-label': {
                fontSize: '15px',
            },
        },
        retroCheck: {
            margin: '7px',
            border: 'unset',
            '& .MuiSvgIcon-root': { fontSize: 20 },
        },
    });
    const classes = useStyles();
    const typoVariant = 'subtitle2';

    const handleRetroactiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setApplyChangeRetroactive(event.target.checked);
    };

    const renderAdjustmentTable = () => (
        <Book>
            <Page>
                <Table className={classes.table} size="small" aria-label="a dense table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Location Name</TableCell>
                            <TableCell>Effective Date</TableCell>
                            <TableCell>Expiration Date</TableCell>
                            <TableCell align="right">Original TIV</TableCell>
                            <TableCell align="right">Updated TIV</TableCell>
                            <TableCell align="right">TIV Delta</TableCell>
                            <TableCell align="right">TRIA %</TableCell>
                            <TableCell align="right">rate</TableCell>
                            <TableCell align="right">Add'l Annual Prem</TableCell>
                            <TableCell align="right">ProRata Prem.</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {adjustmentReviewData?.locationData &&
                            adjustmentReviewData?.locationData.map((row) => (
                                <TableRow key={row.locationName}>
                                    <TableCell component="th" scope="row">
                                        <Typography variant={typoVariant}>{row.locationName}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant={typoVariant}>
                                            {adjustmentReviewData.newEffectiveDate}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant={typoVariant}>{submission.expirationDate}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant={typoVariant}>{formatDollar(row.origTiv, 0)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant={typoVariant}>{formatDollar(row.newTiv, 0)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant={typoVariant}>
                                            {formatDollar(row.newTiv - row.origTiv, 0)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant={typoVariant}>
                                            {formatPercent(row.triaPercentage / 100.0)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant={typoVariant}>{formatDollar(row.finalRate, 3)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant={typoVariant}>{formatDollar(row.grossPrem)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant={typoVariant}>{formatDollar(row.proRataPrem)}</Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        <TableRow key={'total'}>
                            <TableCell colSpan={3}>
                                <FormControlLabel
                                    className={classes.retroLabel}
                                    control={
                                        <Checkbox
                                            className={classes.retroCheck}
                                            checked={applyChangeRetroactive}
                                            onChange={handleRetroactiveChange}
                                            name="retroactiveExposure"
                                        />
                                    }
                                    label="Apply Rate Change To Previous Exposure"
                                />
                            </TableCell>
                            <TableCell colSpan={4} />
                            <TableCell>
                                <Typography>Total Premium</Typography>
                            </TableCell>
                            <TableCell align={'right'}>
                                <Typography variant={typoVariant}>{formatDollar(totalPremium)}</Typography>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Page>
        </Book>
    );

    const updateTaxes = (
        taxDataParam: TaxData,
        totalFees: number,
        totalPrem: number,
        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
    ) => {
        const totalTaxable = totalPrem + totalFees;
        const factor = Math.pow(10, taxDataParam.roundingPrecisionDigits || 2);
        const totalFeesRate = taxDataParam?.totalFeesRate || 0;
        const totalTaxRate = taxDataParam?.totalTaxRate || 0;
        const slaFee = totalFeesRate >= 1 ? totalFeesRate : Math.ceil(totalFeesRate * totalTaxable * factor) / factor;
        const slaTax = Math.ceil(totalTaxRate * totalTaxable * factor) / factor;
        setFieldValue('slaFee', slaFee);
        setFieldValue('slaTax', slaTax);
    };

    const handleUpdateLocationData = (locationData: UwLocationReview[]) => {
        // update the data
        locationData.forEach((ld) => {
            const foundRow = adjustmentReviewData.locationData.find((d) => d.propertyReview?.rowId === ld.rowId);
            if (foundRow) {
                foundRow.propertyReview = ld;
                foundRow.finalRate = ld.finalRate || foundRow.finalRate;
                foundRow.triaPercentage = ld.triaPercentage || foundRow.triaPercentage;
            }
        });
        setAdjustmentReviewData({ ...adjustmentReviewData });
        setDetailsOpen(false);
    };

    const renderAdjustmentForm = () => (
        <Formik
            initialValues={initialValues}
            isInitialValid={false}
            onSubmit={submit}
            validationSchema={adjustmentReviewSchema}
            enableReinitialize
        >
            {({ errors, handleBlur, handleChange, isSubmitting, isValid, touched, values, setFieldValue }) => (
                <Form>
                    <AutoUpdateForm
                        updateTaxes={updateTaxes}
                        isAutoCalc={isAutoCalc}
                        taxData={taxData}
                        totalPrem={totalPremium}
                        isBrokerCommissionRated={adjustmentReviewData?.isBrokerCommissionRated}
                        brokerCommissionPercentage={adjustmentReviewData?.brokerCommissionPercentage}
                    />
                    <Page title="Other Change Review">
                        <Grid container spacing={3}>
                            <Grid md={3} xs={12}>
                                <LabeledData
                                    title="Premium"
                                    value={values.premium ? formatDollar(values.premium) : '--'}
                                />
                            </Grid>
                            <Grid md={3} xs={12}>
                                <LabeledData
                                    title="Broker Commission"
                                    value={
                                        adjustmentReviewData.brokerCommissionPercentage
                                            ? formatPercent(adjustmentReviewData.brokerCommissionPercentage)
                                            : '--'
                                    }
                                />
                            </Grid>
                            <Grid md={4} xs={9}>
                                <LabeledData title="Home State" value={adjustmentReviewData.homeState} />
                            </Grid>
                            <Grid md={2} xs={3} style={{ display: 'flex' }}>
                                <TaxAndFeesInfo
                                    stateCode={adjustmentReviewData.homeState}
                                    onTaxesAndFeesUpdate={(taxDataParam) => {
                                        if (taxesLoading) {
                                            setTaxesLoading(false);
                                            if (taxDataParam.totalTaxRate) {
                                                setIsAutoCalc(true);
                                                setTaxData(taxDataParam);
                                                updateTaxes(
                                                    taxDataParam,
                                                    0,
                                                    calculateAdditionalBrokerPremium(
                                                        adjustmentReviewData?.isBrokerCommissionRated,
                                                        totalPremium,
                                                        adjustmentReviewData?.brokerCommissionPercentage
                                                    ),
                                                    setFieldValue
                                                );
                                            }
                                        }
                                    }}
                                />
                                {
                                    /*remove button if we don't have locationData*/ adjustmentReviewData.locationData &&
                                        adjustmentReviewData.locationData?.filter((ld) => ld.propertyReview)?.length >
                                            0 && (
                                            <IconButton onClick={() => setDetailsOpen(true)} size="large">
                                                <SettingsIcon />
                                            </IconButton>
                                        )
                                }
                            </Grid>
                            {taxesLoading ? (
                                <CircularLoader fillHeight={'25vh'} />
                            ) : (
                                <>
                                    <Grid md={6} xs={12}>
                                        <TextInput
                                            title={`State Tax${isAutoCalc ? '(auto)' : ''}`}
                                            name="slaTax"
                                            placeholder="0.00"
                                            value={values.slaTax}
                                            onChange={(event) => {
                                                setIsAutoCalc(false);
                                                handleChange(event);
                                            }}
                                            onBlur={handleBlur}
                                            mask={currencyMask}
                                            errorMessage={touched.slaTax && errors.slaTax}
                                        />
                                    </Grid>
                                    <Grid md={6} xs={12}>
                                        <TextInput
                                            title={`Stamping Fee${isAutoCalc ? '(auto)' : ''}`}
                                            name="slaFee"
                                            placeholder="0.00"
                                            value={values.slaFee}
                                            onChange={(event) => {
                                                setIsAutoCalc(false);
                                                handleChange(event);
                                            }}
                                            onBlur={handleBlur}
                                            mask={currencyMask}
                                            errorMessage={touched.slaFee && errors.slaFee}
                                        />
                                    </Grid>

                                    <Grid md={6} xs={12}>
                                        <TextInput
                                            title="Policy Fee"
                                            name="policyFee"
                                            placeholder="0.00"
                                            value={values.policyFee}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            mask={currencyMaskWithNegatives}
                                        />
                                    </Grid>
                                    <Grid md={6} xs={12}>
                                        <TextInput
                                            title="Professional Fee"
                                            name="professionalFee"
                                            placeholder="0.00"
                                            value={values.professionalFee}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            mask={currencyMaskWithNegatives}
                                        />
                                    </Grid>
                                    <Grid xs={12}>
                                        <TextInput
                                            value={values.narrative}
                                            name="narrative"
                                            multiline
                                            rows={5}
                                            title="Narrative"
                                            placeholder="Add a message about the change that will be included in the endorsement document"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errorMessage={touched.narrative && errors.narrative}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Page>
                    <Spacing height="32px" />
                    <Grid container spacing={0} alignItems="center">
                        <LrButton lrType="text" onClick={() => setShowDeclineDialog(true)}>
                            Decline
                        </LrButton>
                        <LrButton lrType="primary" type="submit" disabled={!isValid} isLoading={isSubmitting}>
                            Submit
                        </LrButton>
                    </Grid>
                </Form>
            )}
        </Formik>
    );

    return (
        <>
            <Book>
                <EndorsmentNote submissionId={submission.endorsingSubmissionId} />
            </Book>
            {renderAdjustmentTable()}
            <Book
                leftPage={isReadOnly ? <UwAdjustmentReviewPage areTaxesComplete /> : renderAdjustmentForm()}
                rightPage={
                    <Page title="Other Change Request">
                        <LabeledData title="Effective Date" value={submission.effectiveDate} />
                        <LabeledData title="Notes" value={submission.narrative} />
                    </Page>
                }
            />
            {showDeclineDialog && (
                <DeclineReviewDialog
                    action={action}
                    setAction={setAction}
                    submissionId={submission.id}
                    onCancel={() => setShowDeclineDialog(false)}
                    onSubmit={() => history.push(ROUTES.submissions())}
                />
            )}
            {detailsOpen && (
                <UWDetailsAdjustmentDialog
                    onClose={() => setDetailsOpen(false)}
                    onSave={handleUpdateLocationData}
                    isModiedAdjustmentEndorsement={true}
                    isOpen={detailsOpen}
                    data={adjustmentReviewData.locationData.map((d) => ({ ...d.propertyReview })) as UwLocationReview[]}
                    origEffectiveDate={adjustmentReviewData.origEffectiveDate}
                    origExpDate={adjustmentReviewData.origExpirationDate}
                    newEffectiveDate={adjustmentReviewData.newEffectiveDate}
                    newExpDate={adjustmentReviewData.newExpirationDate}
                />
            )}
            <LrDialog
                title={'Taxes and Fees Missing'}
                isOpen={confirmOpen}
                onSubmit={confirmSubmit}
                onCancel={() => setConfirmOpen(false)}
                submitButtonText={'OK'}
            >
                <Typography>Taxes and Fees are both empty, continue anyway?</Typography>
            </LrDialog>
        </>
    );
};

export default AdjustmentReview;
